import { organisationQueryOptions } from "@/server/organisations";
import { dehydrate, HydrationBoundary } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import _ from "lodash";
import { checkIfUserMayRead, ContextUser } from "@/lib/utils";

const ORG_DEFAULT_TAB_PRIORITY: { permission: string | null; path: string }[] = [
  { permission: "SETTINGS", path: "/organisations/$orgId/settings" },
  { permission: "CONTACT_LIST", path: "/organisations/$orgId/contact-list" },
  { permission: "PREDEFINED_TEXT", path: "/organisations/$orgId/predefined-texts" },
  { permission: "USER", path: "/organisations/$orgId/users" },
  { permission: null, path: "/organisations/$orgId/rename" },
  { permission: "USER_LABEL", path: "/organisations/$orgId/user-labels" },
  { permission: null, path: "/organisations/$orgId/emails" },
  { permission: "CALL_SCREENING", path: "/organisations/$orgId/call-screening" },
];

export const Route = createFileRoute("/_dashboard/organisations/$orgId")({
  component: RouteComponent,
  beforeLoad: ({ context, location, params }) => {
    const routeParts = _.compact(location.pathname.split("/"));
    const isExactOrgRoute =
      routeParts[routeParts.length - 2] === "organisations" &&
      (routeParts[routeParts.length - 1] === params.orgId || routeParts[routeParts.length - 1] === `${params.orgId}/`);
    if (isExactOrgRoute) {
      const user = context.user as ContextUser;
      const defaultTab = ORG_DEFAULT_TAB_PRIORITY.find(
        (tab) => tab.permission === null || checkIfUserMayRead(user?.profile, user?.accessControl, tab.permission)
      );
      throw redirect({
        to: defaultTab?.path ?? "/organisations/$orgId/rename",
        params: { orgId: params.orgId },
      });
    }
  },
  loader: async ({ params, context }) => {
    const queryOptions = organisationQueryOptions(Number(params.orgId));

    //We need to prefetch the query in order to get the name for the breadcrumb
    await context.queryClient.prefetchQuery(queryOptions);
    const organisation = context.queryClient.getQueryData(queryOptions.queryKey) as { name?: string } | undefined;

    return {
      crumb: organisation?.name || "Unknown Organisation",
      dehydratedState: dehydrate(context.queryClient) as any,
    };
  },
});

function RouteComponent() {
  const { dehydratedState } = Route.useLoaderData();
  return (
    <HydrationBoundary state={dehydratedState}>
      <Outlet />
    </HydrationBoundary>
  );
}
